import classNames from "classnames";
import { Immutable } from "immer";
import React, { type JSX, useEffect, useRef } from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { ProductOptionSelectorSet } from "../../../components/ProductOptionSelectorSet";
import { ConfiguratorTypes, OptionTypes } from "../../../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../../../models/catalogue";
import { WebPageURL } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { usePageSetting } from "../../../utils/settings";
import { QuantitySelector } from "../QuantitySelector";
import { RootProductSelector } from "../RootProductSelector";
import { defaults } from "../defaults";
import { IModularConfiguratorOptionSet } from "../models";
import { setOptionSelectionErrorState, setOptionValue } from "../reducers";
import {
    availabilityFilteringSelector,
    baseVariantSelector,
    optionCodesSelector,
    rootProductSelector,
} from "../selectors";

const AppSlug = constants["tsicommon.types.AppSlug"];

interface IOptionValue {
    code: IOptionCode;
    value: string | readonly string[];
}

const buildOptionValues = (
    codes: IOptionCode[],
    values: IOptionValues,
): IOptionValue[] => {
    return codes.map((code) => ({
        code: code,
        value: values[code] || "",
    }));
};

const detectOptionChanges = (
    preOptionValues: IOptionValue[],
    optionValues: IOptionValue[],
): IOptionCode[] => {
    const optionCodeChanges: IOptionCode[] = [];
    Array.from(preOptionValues, (option, i) => {
        if (option.value !== optionValues[i].value) {
            optionCodeChanges.push(option.code);
        }
    });
    return optionCodeChanges;
};

const buildErrorMessage = (optionCodeChanges: IOptionCode[]): string => {
    const options = optionCodeChanges.map((o) => {
        // TODO(i18n): Translating string fragments separately breaks sentence structure for non-English languages. Move "option_" prefix stripping to before translation and use full sentence with interpolation
        return o.replace("option_", " ");
    });
    // TODO(i18n): Using join(" and") assumes English word order and hardcodes "and" connector. Some languages need different conjunctions or word order. Use ttag list formatting or include full phrase in translation string
    return interpolate(
        t`Please <strong>confirm mattress %s</strong> before adding to cart.`,
        [options.join(" and")],
    );
};

const buildQuantitySelector = (
    layoutDesign: ConfiguratorTypes,
    disableQuantity: boolean,
): React.ReactNode => {
    if (
        layoutDesign === ConfiguratorTypes.PANEL ||
        layoutDesign === ConfiguratorTypes.UPDATED ||
        disableQuantity
    ) {
        return null;
    }
    return <QuantitySelector />;
};

const buildOptionSelectors = (
    optionCodes: IOptionCode[],
    rootProduct: IProduct | null,
    variant: IProduct | null,
    optionValues: IOptionValues,
    onOptionChange: (
        namespace: string,
        code: IOptionCode,
        index: number,
        totalNumValues: number,
        value: IOptionSingleValue,
    ) => void,
    layoutDesign: ConfiguratorTypes,
    style: "full" | "mini",
    chatLinkComponent: JSX.Element | null | undefined,
    liveChatHeader: string | undefined,
    enableChat: boolean,
    isPlpPanelStyle: boolean | undefined,
    filterAvailable: boolean,
): React.ReactNode[] => {
    return optionCodes.map((code) => (
        <ProductOptionSelectorSet
            key={code}
            selectorID={code}
            rootProduct={rootProduct}
            variant={variant}
            code={code}
            values={optionValues}
            onOptionChange={onOptionChange}
            configuratorType={layoutDesign}
            style={style}
            chatLinkComponent={chatLinkComponent}
            liveChatHeader={liveChatHeader}
            enableChat={enableChat}
            isPlpPanelStyle={isPlpPanelStyle}
            filterAvailable={filterAvailable}
        />
    ));
};

export const ConfiguratorOptions = (
    props: Immutable<{
        optionSet: IModularConfiguratorOptionSet;
        disableQuantity?: boolean;
        style?: "full" | "mini";
        chatLinkComponent?: JSX.Element | null;
        liveChatHeader?: string;
        financingLink?: WebPageURL;
        isPlpPanelStyle?: boolean;
        useCompareDrawerCenter?: boolean;
    }>,
): React.ReactNode => {
    const disableQuantity = props.disableQuantity ?? false;
    const style = props.style ?? "full";

    const rootProduct = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return rootProductSelector(state);
    });

    const variant = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return baseVariantSelector(state);
    });

    const filterAvailable = useAppSelector((rootState) =>
        availabilityFilteringSelector(rootState.configurator || defaults),
    );

    const appSlug = usePageSetting("app_slug");
    const rawOptionCodes = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return optionCodesSelector(
            state,
            props.optionSet.layout.honour_authored_option_order,
        );
    });

    const optionCodes = React.useMemo(() => {
        if (appSlug !== AppSlug.ONE800PILLOWS) {
            return rawOptionCodes;
        }
        const reordered = [...rawOptionCodes];
        const sizeIdx = reordered.indexOf(OptionTypes.SIZE);
        if (sizeIdx > 0) {
            reordered.unshift(reordered.splice(sizeIdx, 1)[0]);
        }
        return reordered;
    }, [appSlug, rawOptionCodes]);

    const optionValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.optionValues;
    });

    const panelTriggerClicked = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.panelTriggerClicked;
    });

    const dispatch = useAppDispatch();

    const onOptionChange = (
        namespace: string,
        code: IOptionCode,
        index: number,
        totalNumValues: number,
        value: IOptionSingleValue,
    ) => {
        dispatch(
            setOptionValue({
                namespace,
                code,
                index,
                totalNumValues,
                value,
            }),
        );
    };

    const prevRootProductRef = useRef<IProduct | null>(null);
    const prevOptionValuesRef = useRef<IOptionValues>({});

    useEffect(() => {
        if (props.optionSet.layout.design !== ConfiguratorTypes.PANEL) {
            return;
        }
        if (!panelTriggerClicked) {
            return;
        }
        if (!rootProduct || !prevRootProductRef.current) {
            prevRootProductRef.current = rootProduct;
            prevOptionValuesRef.current = optionValues;
            return;
        }

        const optionCodes = rootProduct.attributes.product_options?.value || [];
        const preOptionCodes =
            prevRootProductRef.current.attributes.product_options?.value || [];
        const currentOptionValues = buildOptionValues(
            [...optionCodes],
            optionValues,
        );
        const previousOptionValues = buildOptionValues(
            [...preOptionCodes],
            prevOptionValuesRef.current,
        );

        if (
            !props.isPlpPanelStyle &&
            rootProduct.id !== prevRootProductRef.current.id
        ) {
            const optionCodeChanges = detectOptionChanges(
                previousOptionValues,
                currentOptionValues,
            );
            if (optionCodeChanges.length > 0) {
                const errorMessage = buildErrorMessage(optionCodeChanges);
                dispatch(
                    setOptionSelectionErrorState({ reason: errorMessage }),
                );
            }
        }

        prevRootProductRef.current = rootProduct;
        prevOptionValuesRef.current = optionValues;
    }, [
        rootProduct,
        optionValues,
        panelTriggerClicked,
        props.optionSet.layout.design,
        props.isPlpPanelStyle,
        dispatch,
    ]);

    const configuratorBaseClasses = classNames({
        configurator__options: true,
        [`configurator__options--${props.optionSet.layout.design}`]:
            props.optionSet.layout.design,
    });

    return (
        <div className={configuratorBaseClasses}>
            <RootProductSelector
                optionSet={props.optionSet}
                chatLinkComponent={props.chatLinkComponent}
                liveChatHeader={props.liveChatHeader}
                financingLink={props.financingLink}
                isPlpPanelStyle={props.isPlpPanelStyle}
                useCompareDrawerCenter={props.useCompareDrawerCenter}
            />
            {buildOptionSelectors(
                optionCodes,
                rootProduct,
                variant,
                optionValues,
                onOptionChange,
                props.optionSet.layout.design,
                style,
                props.chatLinkComponent,
                props.liveChatHeader,
                props.optionSet.layout.enable_chat,
                props.isPlpPanelStyle,
                filterAvailable,
            )}
            {buildQuantitySelector(
                props.optionSet.layout.design,
                disableQuantity,
            )}
        </div>
    );
};
